/**
 * Navigation Menu Data Structure
 *
 * This file contains the main navigation structure for both desktop and mobile navigation.
 * The data is structured to support multiple levels of navigation with various content types.
 *
 * Structure Overview:
 * - Main navigation items (top level)
 *   - Can have submenu (hasSubmenu: true) or be simple links (hasSubmenu: false)
 *   - Each item must have: id, title, url
 *
 * Submenu Structure Options:
 * 1. Simple children array - Basic list of links
 *    - Each child has: id, title, url
 *    - Optional: children array for third level
 *
 * 2. Sections array - For grouped content (max 5 sections per submenu)
 *    - Each section can have its own children
 *    - Allows for more complex layouts with multiple grouped areas
 *
 * 3. Button type - Special section for CTA buttons
 *    - type: "buttons"
 *    - children array contains button configs with style: "primary" or "ghost"
 *
 * Desktop Layout:
 * - Submenus display in dropdown panels
 * - Maximum 5 columns (sections) per submenu
 * - Sections with children expand vertically
 * - Button sections display as styled CTAs
 *
 * Mobile Layout:
 * - Accordion-style expanding menus
 * - Indented hierarchy for sub-items
 * - Horizontal dividers between sections
 * - Larger touch targets for better usability
 *
 * Test Data Notes:
 * This configuration includes comprehensive test cases:
 * - Items with/without submenus
 * - Various column counts (1-5 max)
 * - Mixed content types in same submenu
 * - Button-only sections
 * - Sections with/without children
 * - Deep nesting examples
 */
export const staticDebugMenuData = [
  {
    id: 1,
    title: "Tuotteet (DEBUG)",
    url: "/tuotteet",
    hasSubmenu: true,
    submenu: [
      {
        id: 11,
        title: "Vakiotuotteet",
        url: "/tuotteet/muotolevyt/vakio",
        children: [
          {
            id: 111,
            title: "Standard-muotolevyt",
            url: "/tuotteet/muotolevyt/standard",
          },
          {
            id: 112,
            title: "Premium-muotolevyt",
            url: "/tuotteet/muotolevyt/premium",
          },
          {
            id: 113,
            title: "Basic-muotolevyt",
            url: "/tuotteet/muotolevyt/basic",
          },
          {
            id: 114,
            title: "Economy-muotolevyt",
            url: "/tuotteet/muotolevyt/economy",
          },
        ],
      },
      {
        id: 12,
        title: "Erikoistuotteet",
        url: "/tuotteet/muotolevyt/erikois",
        children: [
          {
            id: 121,
            title: "Palosuojatut",
            url: "/tuotteet/muotolevyt/palosuojatut",
          },
          {
            id: 122,
            title: "Kosteudenkestävät",
            url: "/tuotteet/muotolevyt/kosteudenkesto",
          },
          {
            id: 123,
            title: "Akustiset",
            url: "/tuotteet/muotolevyt/akustiset",
          },
          {
            id: 124,
            title: "Antibakteeriset",
            url: "/tuotteet/muotolevyt/antibakteeriset",
          },
          {
            id: 125,
            title: "UV-suojatut",
            url: "/tuotteet/muotolevyt/uv-suojatut",
          },
        ],
      },
      {
        id: 13,
        title: "Laminaatit",
        url: "/tuotteet/laminaatit",
        children: [
          { id: 131, title: "HPL-laminaatit", url: "/tuotteet/laminaatit/hpl" },
          { id: 132, title: "CPL-laminaatit", url: "/tuotteet/laminaatit/cpl" },
          {
            id: 133,
            title: "Kompaktilaminaatit",
            url: "/tuotteet/laminaatit/kompakti",
          },
        ],
      },
      {
        id: 14,
        title: "Työstöpalvelut",
        url: "/tuotteet/tyostopalvelut",
        children: [
          { id: 141, title: "CNC-työstö", url: "/tuotteet/tyostopalvelut/cnc" },
          {
            id: 142,
            title: "Reunanauhat",
            url: "/tuotteet/tyostopalvelut/reunanauhat",
          },
          {
            id: 143,
            title: "Sahauspalvelut",
            url: "/tuotteet/tyostopalvelut/sahaus",
          },
          {
            id: 144,
            title: "Porauspalvelut",
            url: "/tuotteet/tyostopalvelut/poraus",
          },
          {
            id: 145,
            title: "Jyrsintäpalvelut",
            url: "/tuotteet/tyostopalvelut/jyrsinta",
          },
          {
            id: 146,
            title: "Laserleikkaus",
            url: "/tuotteet/tyostopalvelut/laser",
          },
        ],
      },
      {
        id: 15,
        type: "buttons",
        children: [
          {
            id: 151,
            title: "Tilaa näytteet",
            url: "/tilaa-naytteet",
            style: "primary",
          },
          {
            id: 152,
            title: "Lataa katalogi",
            url: "/lataa-katalogi",
            style: "ghost",
          },
          {
            id: 153,
            title: "Pyydä tarjous",
            url: "/pyyda-tarjous",
            style: "primary",
          },
        ],
      },
    ],
  },
  {
    id: 2,
    title: "Palvelut (DEBUG)",
    url: "/palvelut",
    hasSubmenu: true,
    submenu: [
      {
        id: 21,
        title: "Suunnittelupalvelut",
        url: "/palvelut/suunnittelu",
        description: "Ammattitaitoinen suunnittelu",
        children: [
          { id: 211, title: "3D-suunnittelu", url: "/palvelut/suunnittelu/3d" },
          {
            id: 212,
            title: "Tekninen tuki",
            url: "/palvelut/suunnittelu/tekninen-tuki",
          },
          {
            id: 213,
            title: "Materiaalikonsultointi",
            url: "/palvelut/suunnittelu/konsultointi",
          },
          {
            id: 214,
            title: "Projektisuunnittelu",
            url: "/palvelut/suunnittelu/projekti",
          },
        ],
      },
      {
        id: 22,
        title: "Logistiikka",
        url: "/palvelut/logistiikka",
        description: "Luotettavat toimitukset",
        children: [
          {
            id: 221,
            title: "Kotimaankuljetukset",
            url: "/palvelut/logistiikka/kotimaa",
          },
          {
            id: 222,
            title: "Varastopalvelut",
            url: "/palvelut/logistiikka/varasto",
          },
          {
            id: 223,
            title: "Pikatoimitukset",
            url: "/palvelut/logistiikka/pika",
          },
          {
            id: 224,
            title: "Kansainväliset toimitukset",
            url: "/palvelut/logistiikka/kv",
          },
        ],
      },
      {
        id: 23,
        title: "Koulutukset",
        url: "/palvelut/koulutukset",
        children: [
          {
            id: 231,
            title: "Tuotekoulutukset",
            url: "/palvelut/koulutukset/tuote",
          },
          {
            id: 232,
            title: "Asennuskoulutukset",
            url: "/palvelut/koulutukset/asennus",
          },
          {
            id: 233,
            title: "Verkkokoulutukset",
            url: "/palvelut/koulutukset/verkko",
          },
        ],
      },
      { id: 24, title: "Huoltopalvelut", url: "/palvelut/huolto" },
      {
        id: 25,
        title: "Lisäpalvelut",
        url: "/palvelut/lisapalvelut",
        sections: [
          {
            id: 251,
            title: "Mittauspalvelut",
            url: "/palvelut/lisapalvelut/mittaus",
            children: [
              { id: 2511, title: "3D-mittaus", url: "/palvelut/mittaus/3d" },
              {
                id: 2512,
                title: "Lasermittaus",
                url: "/palvelut/mittaus/laser",
              },
            ],
          },
          {
            id: 252,
            title: "Konsultointi",
            url: "/palvelut/lisapalvelut/konsultointi",
          },
          {
            id: 253,
            title: "Etäpalvelut",
            url: "/palvelut/lisapalvelut/eta",
            children: [
              { id: 2531, title: "Etätuki", url: "/palvelut/eta/tuki" },
              { id: 2532, title: "Etäkoulutus", url: "/palvelut/eta/koulutus" },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 3,
    title: "Ratkaisut (DEBUG)",
    url: "/ratkaisut",
    hasSubmenu: true,
    submenu: [
      {
        id: 31,
        title: "Toimialoittain",
        url: "/ratkaisut/toimialat",
        sections: [
          {
            id: 311,
            title: "Kauppa ja retail",
            url: "/ratkaisut/kauppa",
            children: [
              {
                id: 3111,
                title: "Myymäläkalusteet",
                url: "/ratkaisut/kauppa/kalusteet",
              },
              {
                id: 3112,
                title: "Kassapisteet",
                url: "/ratkaisut/kauppa/kassa",
              },
              {
                id: 3113,
                title: "Varastoratkaisut",
                url: "/ratkaisut/kauppa/varasto",
              },
            ],
          },
          {
            id: 312,
            title: "Terveydenhuolto",
            url: "/ratkaisut/terveys",
            children: [
              {
                id: 3121,
                title: "Hygieniaratkaisut",
                url: "/ratkaisut/terveys/hygienia",
              },
              {
                id: 3122,
                title: "Laboratoriokalusteet",
                url: "/ratkaisut/terveys/lab",
              },
            ],
          },
          {
            id: 313,
            title: "Koulutus",
            url: "/ratkaisut/koulutus",
            children: [
              {
                id: 3131,
                title: "Luokkahuoneet",
                url: "/ratkaisut/koulutus/luokat",
              },
              {
                id: 3132,
                title: "Auditoriot",
                url: "/ratkaisut/koulutus/auditoriot",
              },
            ],
          },
        ],
      },
      {
        id: 32,
        title: "Käyttökohteittain",
        url: "/ratkaisut/kayttokohteet",
        children: [
          { id: 321, title: "Keittiöt", url: "/ratkaisut/keittiot" },
          { id: 322, title: "Kylpyhuoneet", url: "/ratkaisut/kylpyhuoneet" },
          { id: 323, title: "Toimistot", url: "/ratkaisut/toimistot" },
          { id: 324, title: "Julkitilat", url: "/ratkaisut/julkitilat" },
        ],
      },
      {
        id: 33,
        type: "buttons",
        children: [
          {
            id: 331,
            title: "Lataa suunnitteluopas",
            url: "/suunnitteluopas",
            style: "ghost",
          },
          {
            id: 332,
            title: "Ota yhteyttä asiantuntijaan",
            url: "/asiantuntija",
            style: "primary",
          },
        ],
      },
    ],
  },
  {
    id: 4,
    title: "Yritys (DEBUG)",
    url: "/yritys",
    hasSubmenu: true,
    submenu: [
      {
        id: 41,
        title: "Tietoa meistä",
        url: "/yritys/tietoa",
        description: "Tutusta yritykseemme",
      },
      {
        id: 42,
        title: "Historia",
        url: "/yritys/historia",
        description: "Yrityksemme tarina",
        children: [
          {
            id: 421,
            title: "Vuosikymmenet",
            url: "/yritys/historia/vuosikymmenet",
          },
          {
            id: 422,
            title: "Merkkipaalut",
            url: "/yritys/historia/merkkipaalut",
          },
        ],
      },
      {
        id: 43,
        title: "Vastuullisuus",
        url: "/yritys/vastuullisuus",
        description: "Vastuullista toimintaa",
        children: [
          {
            id: 431,
            title: "Ympäristö",
            url: "/yritys/vastuullisuus/ymparisto",
          },
          {
            id: 432,
            title: "Sertifikaatit",
            url: "/yritys/vastuullisuus/sertifikaatit",
          },
          {
            id: 433,
            title: "Kierrätys",
            url: "/yritys/vastuullisuus/kierratys",
          },
        ],
      },
      {
        id: 44,
        title: "Ura",
        url: "/yritys/ura",
        description: "Avoimet työpaikat",
        children: [
          { id: 441, title: "Avoimet paikat", url: "/yritys/ura/avoimet" },
          { id: 442, title: "Työntekijätarinat", url: "/yritys/ura/tarinat" },
          { id: 443, title: "Harjoittelu", url: "/yritys/ura/harjoittelu" },
        ],
      },
      {
        id: 45,
        title: "Medialle",
        url: "/yritys/media",
        children: [
          { id: 451, title: "Tiedotteet", url: "/yritys/media/tiedotteet" },
          { id: 452, title: "Kuvapankki", url: "/yritys/media/kuvat" },
        ],
      },
    ],
  },
  {
    id: 5,
    title: "Inspiraatio (DEBUG)",
    url: "/inspiraatio",
    hasSubmenu: true,
    submenu: [
      {
        id: 51,
        title: "Projektit",
        url: "/inspiraatio/projektit",
        children: [
          { id: 511, title: "Kohteet", url: "/inspiraatio/projektit/kohteet" },
          {
            id: 512,
            title: "Referenssit",
            url: "/inspiraatio/projektit/referenssit",
          },
          {
            id: 513,
            title: "Case-esimerkit",
            url: "/inspiraatio/projektit/case",
          },
        ],
      },
      {
        id: 52,
        title: "Trendit",
        url: "/inspiraatio/trendit",
        children: [
          {
            id: 521,
            title: "Väritrendit 2025",
            url: "/inspiraatio/trendit/varit",
          },
          {
            id: 522,
            title: "Materiaalitrendit",
            url: "/inspiraatio/trendit/materiaalit",
          },
        ],
      },
      {
        id: 53,
        title: "Suunnittelutyökalut",
        url: "/inspiraatio/tyokalut",
        sections: [
          {
            id: 531,
            title: "Värikartat",
            url: "/inspiraatio/tyokalut/varikartat",
          },
          {
            id: 532,
            title: "Tekstuurit",
            url: "/inspiraatio/tyokalut/tekstuurit",
          },
          { id: 533, title: "3D-mallit", url: "/inspiraatio/tyokalut/3d" },
        ],
      },
    ],
  },
  {
    id: 6,
    title: "Tuki (DEBUG)",
    url: "/tuki",
    hasSubmenu: true,
    submenu: [
      {
        id: 61,
        title: "Asiakaspalvelu",
        url: "/tuki/asiakaspalvelu",
        children: [
          { id: 611, title: "Yhteystiedot", url: "/tuki/yhteystiedot" },
          { id: 612, title: "Aukioloajat", url: "/tuki/aukioloajat" },
          { id: 613, title: "Chat-tuki", url: "/tuki/chat" },
        ],
      },
      {
        id: 62,
        title: "Dokumentit",
        url: "/tuki/dokumentit",
        children: [
          {
            id: 621,
            title: "Tekniset tiedot",
            url: "/tuki/dokumentit/tekniset",
          },
          { id: 622, title: "Asennusohjeet", url: "/tuki/dokumentit/asennus" },
          { id: 623, title: "Huolto-ohjeet", url: "/tuki/dokumentit/huolto" },
          { id: 624, title: "Takuuehdot", url: "/tuki/dokumentit/takuu" },
        ],
      },
      { id: 63, title: "Usein kysytyt", url: "/tuki/ukk" },
    ],
  },
  {
    id: 7,
    title: "Uutiset (DEBUG)",
    url: "/uutiset",
    hasSubmenu: true,
    submenu: [
      { id: 71, title: "Ajankohtaista", url: "/uutiset/ajankohtaista" },
      {
        id: 72,
        title: "Blogikirjoitukset",
        url: "/uutiset/blogi",
        children: [
          {
            id: 721,
            title: "Asiantuntija-artikkelit",
            url: "/uutiset/blogi/asiantuntija",
          },
          { id: 722, title: "Vinkit ja ohjeet", url: "/uutiset/blogi/vinkit" },
        ],
      },
      {
        id: 73,
        title: "Tapahtumat",
        url: "/uutiset/tapahtumat",
        children: [
          { id: 731, title: "Messut", url: "/uutiset/tapahtumat/messut" },
          {
            id: 732,
            title: "Webinaarit",
            url: "/uutiset/tapahtumat/webinaarit",
          },
          {
            id: 733,
            title: "Koulutukset",
            url: "/uutiset/tapahtumat/koulutukset",
          },
        ],
      },
    ],
  },
  {
    id: 8,
    title: "Yhteystiedot (DEBUG)",
    url: "/yhteystiedot",
    hasSubmenu: false,
  },
  {
    id: 9,
    title: "Verkkokauppa (DEBUG)",
    url: "https://verkkokauppa.muotolevy.fi",
    hasSubmenu: false,
  },
  { id: 10, title: "Extranet (DEBUG)", url: "/extranet", hasSubmenu: false },
];

export const ctaButtons = [];
